import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    AlertTriangle, CheckCircle2, XCircle, ExternalLink, FileEdit,
    FileText, Download, Receipt, Clock,
} from "lucide-react";
import { resolveBillStatus } from "@/lib/statusResolvers";
import TollTransactionDetailModal from "@/components/TollTransactionDetailModal";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) { if (!s) return "—"; return String(s).slice(0, 10); }
function fmtISO(s) { return s ? String(s).replace("T", " ").slice(0, 19) : "—"; }

/**
 * Phase TOLLBILL-P5c (2026-07-10) — Toll bill detail drawer.
 *
 * Bills are the primary entity in P5a. This modal mirrors the
 * structural pattern from TollTransactionDetailModal (P3a/P4b) but is
 * scoped to the bill lifecycle:
 *   * fee breakdown table (toll / processing / late / previous balance)
 *   * child transactions list — click to drill into the txn modal
 *   * attachment section (JWT-authenticated blob download)
 *   * raw payload JSON (collapsed)
 *   * actions: Reassign, Mark Disputed, Resolve Dispute
 *
 * Terminal states (invoiced/void) render a read-only banner and hide
 * action buttons. The bill-level endpoints handle cascade to child txns.
 */
export default function TollBillDetailModal({ open, onOpenChange, billId, canManage, onChanged }) {
    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [disputeOpen, setDisputeOpen] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);

    const [customerId, setCustomerId] = useState("");
    const [rentalId, setRentalId] = useState("");
    const [disputeReason, setDisputeReason] = useState("");
    const [resolveOutcome, setResolveOutcome] = useState("billable");
    const [resolveNote, setResolveNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [attachDownloading, setAttachDownloading] = useState(false);

    // Child txn drill-down modal state
    const [childTxnId, setChildTxnId] = useState(null);
    const [childOpen, setChildOpen] = useState(false);

    useEffect(() => {
        if (!open || !billId) return;
        setLoading(true);
        setReassignOpen(false); setDisputeOpen(false); setResolveOpen(false);
        setDisputeReason(""); setResolveNote(""); setResolveOutcome("billable");
        api.get(`/admin/tolls/bills/${encodeURIComponent(billId)}`)
            .then(({ data }) => {
                setBill(data);
                setCustomerId(data.customer_id || "");
                setRentalId(data.rental_id || "");
            })
            .catch((err) => { toast.error(formatApiError(err)); onOpenChange(false); })
            .finally(() => setLoading(false));
    }, [open, billId, onOpenChange]);

    const reload = async () => {
        try {
            const { data } = await api.get(`/admin/tolls/bills/${encodeURIComponent(billId)}`);
            setBill(data);
            if (onChanged) onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const submitReassign = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/tolls/bills/${encodeURIComponent(bill.bill_id)}`, {
                customer_id: customerId.trim() || null,
                rental_id: rentalId.trim() || null,
            });
            toast.success("Reassigned");
            setReassignOpen(false);
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const submitDispute = async () => {
        if (!disputeReason.trim()) { toast.error("Reason is required"); return; }
        setSaving(true);
        try {
            await api.post(`/admin/tolls/bills/${encodeURIComponent(bill.bill_id)}/mark-disputed`, {
                reason: disputeReason.trim(),
            });
            toast.success("Marked as disputed — children cascaded");
            setDisputeOpen(false);
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const submitResolve = async () => {
        setSaving(true);
        try {
            await api.post(`/admin/tolls/bills/${encodeURIComponent(bill.bill_id)}/resolve-dispute`, {
                outcome: resolveOutcome,
                note: resolveNote.trim() || null,
            });
            toast.success(`Dispute resolved: ${resolveOutcome}`);
            setResolveOpen(false);
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const downloadAttachment = async () => {
        if (!bill?.attachment_file_id) return;
        setAttachDownloading(true);
        try {
            const resp = await api.get(
                `/admin/tolls/bills/${encodeURIComponent(bill.bill_id)}/attachment`,
                { responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = bill.attachment_filename || `toll-bill-${bill.bill_id}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            toast.success("Downloaded");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setAttachDownloading(false); }
    };

    const openAttachmentInNewTab = async () => {
        if (!bill?.attachment_file_id) return;
        setAttachDownloading(true);
        try {
            const resp = await api.get(
                `/admin/tolls/bills/${encodeURIComponent(bill.bill_id)}/attachment`,
                { responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setAttachDownloading(false); }
    };

    const status = bill ? resolveBillStatus(bill) : null;
    const feeRows = bill ? [
        { key: "toll",     label: "Tolls",             cents: bill.toll_amount_cents || 0 },
        { key: "proc",     label: "Processing fee",    cents: bill.processing_fee_cents || 0 },
        { key: "late",     label: "Late fees",         cents: bill.late_fees_cents || 0 },
        { key: "prev",     label: "Previous balance",  cents: bill.previous_balance_cents || 0 },
    ] : [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="bill-detail-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 flex-wrap">
                        <Receipt size={16} className="text-blue-900" />
                        Toll bill
                        {status && (
                            <span
                                data-testid="bill-detail-status-chip"
                                className={`inline-flex items-center rounded-full ring-1 px-2 py-0.5 text-[11px] font-semibold ${status.tone}`}
                            >
                                {status.label}
                            </span>
                        )}
                        {status?.isOverdue && (
                            <span className="inline-flex items-center gap-1 text-red-700 text-[11px] font-medium" data-testid="bill-detail-overdue-flag">
                                <Clock size={12} /> Overdue
                            </span>
                        )}
                        {bill?.dispute_flag && !status?.isDisputed && (
                            <span className="inline-flex items-center gap-1 text-red-700 text-[11px] font-medium">
                                <AlertTriangle size={12} /> Dispute flag
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        {bill ? (
                            <span>
                                <span className="font-mono">{bill.bill_id}</span>
                                {bill.document_type && <span className="text-slate-500"> · {bill.document_type}</span>}
                            </span>
                        ) : "Loading…"}
                    </DialogDescription>
                </DialogHeader>

                {loading && <p className="text-sm text-slate-500 py-6" data-testid="bill-detail-loading">Loading…</p>}

                {!loading && bill && (
                    <div className="space-y-4">
                        {/* Two-column body */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Left col — metadata */}
                            <div className="space-y-2 text-sm">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                    <div>
                                        <Label className="text-xs text-slate-500">Plate</Label>
                                        <p className="text-slate-900 font-mono" data-testid="bill-detail-plate">
                                            {bill.plate || "—"}
                                            {bill.plate_state && <span className="text-slate-500"> · {bill.plate_state}</span>}
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Authority</Label>
                                        <p className="text-slate-900" data-testid="bill-detail-authority">{bill.authority || "—"}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Bill date</Label>
                                        <p className="text-slate-900" data-testid="bill-detail-bill-date">{fmtDate(bill.bill_date)}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Due date</Label>
                                        <p className={`tabular-nums ${status?.isOverdue ? "text-red-700 font-semibold" : "text-slate-900"}`} data-testid="bill-detail-due-date">
                                            {fmtDate(bill.due_date)}
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Owner</Label>
                                        <p className="text-slate-900 truncate" title={bill.owner || ""}>{bill.owner || "—"}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">External source</Label>
                                        <p className="text-slate-900">{bill.external_source || "—"}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
                                    <div>
                                        <Label className="text-xs text-slate-500">Customer</Label>
                                        <p className="text-slate-900" data-testid="bill-detail-customer">
                                            {bill.customer_id ? (
                                                <Link to={`/admin/customers/${bill.customer_id}`} className="text-blue-900 hover:underline inline-flex items-center gap-1">
                                                    {bill.customer_name || `${bill.customer_id.slice(0, 8)}…`} <ExternalLink size={11} />
                                                </Link>
                                            ) : <span className="text-slate-400">Unresolved</span>}
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Rental</Label>
                                        <p className="text-slate-900" data-testid="bill-detail-rental">
                                            {bill.rental_id ? (
                                                <Link to={`/admin/rentals/${bill.rental_id}`} className="text-blue-900 hover:underline inline-flex items-center gap-1">
                                                    {bill.rental_unit_number || `${bill.rental_id.slice(0, 8)}…`} <ExternalLink size={11} />
                                                </Link>
                                            ) : <span className="text-slate-400">Unresolved</span>}
                                        </p>
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-xs text-slate-500">Invoice</Label>
                                        <p className="text-slate-900" data-testid="bill-detail-invoice">
                                            {bill.invoice_id ? (
                                                <Link to={`/admin/invoices/${bill.invoice_id}`} className="text-blue-900 hover:underline inline-flex items-center gap-1">
                                                    View invoice <ExternalLink size={11} />
                                                </Link>
                                            ) : <span className="text-slate-400">Not yet invoiced</span>}
                                        </p>
                                    </div>
                                </div>
                                {bill.dispute_reason && (
                                    <div>
                                        <Label className="text-xs text-slate-500">Dispute reason</Label>
                                        <p className="text-slate-900 text-sm rounded-md bg-red-50 border border-red-100 px-3 py-2" data-testid="bill-detail-dispute-reason">
                                            {bill.dispute_reason}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Right col — fee breakdown */}
                            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="bill-detail-fee-breakdown">
                                <p className="text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Fee breakdown</p>
                                <table className="min-w-full text-sm">
                                    <tbody>
                                        {feeRows.map((r) => {
                                            const muted = r.cents === 0;
                                            return (
                                                <tr key={r.key} data-testid={`bill-detail-fee-row-${r.key}`} className={muted ? "text-slate-400" : "text-slate-800"}>
                                                    <td className="py-1.5 pr-3">{r.label}</td>
                                                    <td className="py-1.5 text-right tabular-nums font-medium">{fmtMoney(r.cents)}</td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="border-t border-slate-200">
                                            <td className="py-2 pr-3 font-semibold text-slate-900">Total due</td>
                                            <td className="py-2 text-right tabular-nums font-bold text-slate-900" data-testid="bill-detail-total-due">
                                                {fmtMoney(bill.total_due_cents)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Child transactions */}
                        <div className="rounded-md border border-slate-200 bg-white" data-testid="bill-detail-children">
                            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                                <p className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                                    Child transactions ({(bill.transactions || []).length})
                                </p>
                                <p className="text-[11px] text-slate-500">Click a row to drill in</p>
                            </div>
                            {(bill.transactions || []).length === 0 ? (
                                <p className="text-sm text-slate-500 p-4" data-testid="bill-detail-no-children">No child transactions.</p>
                            ) : (
                                <table className="min-w-full text-sm">
                                    <thead className="text-[11px] text-slate-500 uppercase">
                                        <tr>
                                            <th className="text-left py-1.5 px-3">Posted at</th>
                                            <th className="text-left py-1.5 px-3">Plate</th>
                                            <th className="text-left py-1.5 px-3">Location</th>
                                            <th className="text-right py-1.5 px-3">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {(bill.transactions || []).map((t) => (
                                            <tr
                                                key={t.transaction_id}
                                                onClick={() => { setChildTxnId(t.transaction_id); setChildOpen(true); }}
                                                className="cursor-pointer hover:bg-slate-50/70"
                                                data-testid={`bill-detail-child-${t.transaction_id}`}
                                            >
                                                <td className="py-1.5 px-3 text-slate-700 whitespace-nowrap">{fmtISO(t.posted_at)}</td>
                                                <td className="py-1.5 px-3 font-mono text-slate-700">{t.plate || "—"}</td>
                                                <td className="py-1.5 px-3 text-slate-800 truncate max-w-[200px]" title={t.location || ""}>{t.location || "—"}</td>
                                                <td className="py-1.5 px-3 text-right tabular-nums font-medium">{fmtMoney(t.amount_cents)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Raw payload + Attachment */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <details className="rounded-md border border-slate-200 bg-slate-50/50 text-xs">
                                <summary className="cursor-pointer font-medium text-slate-600 px-3 py-1.5" data-testid="bill-detail-raw-toggle">
                                    Raw payload (JSON)
                                </summary>
                                <pre className="text-[10.5px] font-mono text-slate-700 px-3 py-2 whitespace-pre-wrap max-h-56 overflow-auto" data-testid="bill-detail-raw">
{JSON.stringify(bill.raw_payload || {}, null, 2)}
                                </pre>
                            </details>

                            <div className="rounded-md border border-slate-200 bg-white p-3 text-xs" data-testid="bill-detail-attachment-section">
                                <p className="font-medium text-slate-600 flex items-center gap-1.5 mb-2">
                                    <FileText size={13} /> Bill PDF
                                </p>
                                {bill.has_attachment || bill.attachment_file_id ? (
                                    <div className="space-y-2" data-testid="bill-detail-attachment-present">
                                        <p className="text-slate-800 truncate" title={bill.attachment_filename || ""} data-testid="bill-detail-attachment-filename">
                                            {bill.attachment_filename || "toll-bill.pdf"}
                                        </p>
                                        <div className="flex gap-1.5 flex-wrap">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={openAttachmentInNewTab}
                                                disabled={attachDownloading}
                                                className="gap-1 h-7 px-2 text-[11px]"
                                                data-testid="bill-detail-attachment-view"
                                            >
                                                <ExternalLink size={11} /> View
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={downloadAttachment}
                                                disabled={attachDownloading}
                                                className="bg-blue-900 hover:bg-blue-800 gap-1 h-7 px-2 text-[11px]"
                                                data-testid="bill-detail-attachment-download"
                                            >
                                                <Download size={11} /> {attachDownloading ? "…" : "Download PDF"}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-slate-400 italic" data-testid="bill-detail-attachment-empty">
                                        No bill PDF attached
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Action panels */}
                        {canManage && !status?.isTerminal && (
                            <div className="space-y-2 border-t border-slate-100 pt-4">
                                <div className="flex flex-wrap gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setReassignOpen((v) => !v)} data-testid="bill-detail-reassign-toggle" className="gap-1.5">
                                        <FileEdit size={14} /> Reassign
                                    </Button>
                                    {!status?.isDisputed && (
                                        <Button variant="outline" size="sm" onClick={() => { setDisputeOpen((v) => !v); setResolveOpen(false); }} data-testid="bill-detail-dispute-toggle" className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50">
                                            <AlertTriangle size={14} /> Mark disputed
                                        </Button>
                                    )}
                                    {status?.isDisputed && (
                                        <Button variant="outline" size="sm" onClick={() => { setResolveOpen((v) => !v); setDisputeOpen(false); }} data-testid="bill-detail-resolve-toggle" className="gap-1.5">
                                            <CheckCircle2 size={14} /> Resolve dispute
                                        </Button>
                                    )}
                                </div>

                                {reassignOpen && (
                                    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid="bill-detail-reassign-panel">
                                        <div>
                                            <Label className="text-xs">Customer ID</Label>
                                            <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="uuid or blank to clear" data-testid="bill-detail-reassign-customer" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Rental ID</Label>
                                            <Input value={rentalId} onChange={(e) => setRentalId(e.target.value)} placeholder="uuid or blank to clear" data-testid="bill-detail-reassign-rental" />
                                        </div>
                                        <div className="flex justify-end gap-2 pt-1">
                                            <Button variant="ghost" size="sm" onClick={() => setReassignOpen(false)}>Cancel</Button>
                                            <Button size="sm" onClick={submitReassign} disabled={saving} data-testid="bill-detail-reassign-save">Save</Button>
                                        </div>
                                    </div>
                                )}
                                {disputeOpen && (
                                    <div className="rounded-md border border-red-200 bg-red-50/40 p-3 space-y-2" data-testid="bill-detail-dispute-panel">
                                        <Label className="text-xs">Reason (required) — will cascade to child transactions</Label>
                                        <Textarea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="e.g. Wrong plate captured on all transactions" data-testid="bill-detail-dispute-reason" rows={2} />
                                        <div className="flex justify-end gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => setDisputeOpen(false)}>Cancel</Button>
                                            <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={submitDispute} disabled={saving} data-testid="bill-detail-dispute-submit">Mark disputed</Button>
                                        </div>
                                    </div>
                                )}
                                {resolveOpen && (
                                    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3" data-testid="bill-detail-resolve-panel">
                                        <div>
                                            <Label className="text-xs">Outcome</Label>
                                            <div className="flex gap-2 mt-1">
                                                <Button variant={resolveOutcome === "billable" ? "default" : "outline"} size="sm" onClick={() => setResolveOutcome("billable")} data-testid="bill-detail-resolve-billable" className="gap-1.5">
                                                    <CheckCircle2 size={14} /> Billable
                                                </Button>
                                                <Button variant={resolveOutcome === "voided" ? "default" : "outline"} size="sm" onClick={() => setResolveOutcome("voided")} data-testid="bill-detail-resolve-voided" className="gap-1.5">
                                                    <XCircle size={14} /> Voided
                                                </Button>
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-1">
                                                {resolveOutcome === "billable"
                                                    ? "Restores the bill (and children) so it can be included in the next invoice run."
                                                    : "Removes the bill and its children from future invoicing entirely."}
                                            </p>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Note (optional)</Label>
                                            <Textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder="Context for the audit log" data-testid="bill-detail-resolve-note" rows={2} />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => setResolveOpen(false)}>Cancel</Button>
                                            <Button size="sm" onClick={submitResolve} disabled={saving} data-testid="bill-detail-resolve-submit">Resolve</Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {status?.isTerminal && (
                            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600" data-testid="bill-detail-terminal-banner">
                                Terminal state — this bill can no longer be edited.
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bill-detail-close">Close</Button>
                </DialogFooter>

                {/* Child transaction drill-down modal */}
                <TollTransactionDetailModal
                    open={childOpen}
                    onOpenChange={setChildOpen}
                    txnId={childTxnId}
                    canManage={false /* dispute lives at the bill level in Phase 5a+ */}
                    onChanged={reload}
                />
            </DialogContent>
        </Dialog>
    );
}
